import posthog, { type CaptureResult } from "posthog-js";
import { REPLAY_MASKING } from "@/lib/tracking/replayMasking";

type ExceptionFrame = { filename?: string | null };
type ExceptionListEntry = {
  type?: string | null;
  value?: string | null;
  stacktrace?: { frames?: ExceptionFrame[] | null } | null;
};

/**
 * Decide whether a captured `$exception` event is opaque enough to drop.
 *
 * Cross-origin script failures surface in the browser as the infamous
 * "Script error." with no message, no type, and no stack the runtime will hand us
 * — an unactionable event we can't triage or deminify. These fire in volume
 * (~93 events / 89 users on the homepage) and bury real issues. We drop only the
 * provably-empty ones: an exact "Script error." (or empty / "undefined") message
 * AND no stack frame carrying a resolvable filename. Anything with a real message
 * or a located frame is always kept.
 *
 * Field names verified against posthog-js 1.374.2: a `$exception` event stores its
 * detail under `properties.$exception_list`, an array of
 * `{ type, value, stacktrace: { frames: [{ filename, ... }] } }` entries. The
 * modern capture path writes the message to `$exception_list[0].value`; the legacy
 * sentry path also mirrors it to the top-level `properties.$exception_message`,
 * which we read as a fallback.
 *
 * Pure and exported so the drop decision is unit-testable in isolation.
 */
export function shouldDropException(event: CaptureResult | null): boolean {
  if (!event || event.event !== "$exception") return false;

  const props = event.properties ?? {};
  const list: ExceptionListEntry[] = Array.isArray(props.$exception_list)
    ? props.$exception_list
    : [];
  const first = list[0] ?? {};

  const rawMessage = first.value ?? props.$exception_message ?? "";
  const message = String(rawMessage).trim();
  const isOpaqueMessage =
    message === "" || message === "undefined" || message === "Script error.";
  if (!isOpaqueMessage) return false;

  // A resolvable filename anywhere in any exception's stack means the error is
  // attributable and worth keeping, even when the message itself is opaque.
  const hasResolvableFrame = list.some((entry) => {
    const frames = entry?.stacktrace?.frames;
    return (
      Array.isArray(frames) &&
      frames.some((frame) => Boolean(frame?.filename && String(frame.filename).trim()))
    );
  });

  return !hasResolvableFrame;
}

if (process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    capture_exceptions: true,
    // Defensive drop hook: strips opaque cross-origin "Script error." exceptions that
    // carry no message and no located stack frame (see shouldDropException). Wrapped so
    // a throw in the decision never blocks a legitimate event — on any error we keep it.
    // before_send only runs in production (this whole init block is prod-gated).
    before_send: (event) => {
      try {
        return shouldDropException(event) ? null : event;
      } catch {
        return event;
      }
    },
    // Dead-click capture was off (the defaults preset doesn't enable it), so
    // $dead_click had zero events project-wide — we lost a key frustration
    // signal on the marketing/landing/signup surface. The .ph-no-capture class
    // (set as session_recording.blockSelector below) also suppresses dead clicks,
    // so masked UI is covered. NOTE: canvas/WebGL tool surfaces (paint booth,
    // viewers) can generate high $dead_click volume because canvas clicks don't
    // mutate the watched DOM — add `ph-no-deadclick`/`ph-no-capture` to those
    // editor containers if event volume/cost climbs.
    capture_dead_clicks: true,
    // Session replay with privacy-first masking (shared with the BlinkLab frame
    // recorder — see REPLAY_MASKING). Canvas/WebGL content (paint booth, 3D
    // viewports) is captured only when "Capture canvas" is on in PostHog →
    // Settings → Session replay; that toggle is remote-only in posthog-js (no
    // client option here). Every WebGL viewer must set preserveDrawingBuffer: true:
    // without it the recorder clears the canvas before each snapshot, which records
    // blank and flickers the live viewport. Canvases inside iframes are invisible
    // to this recorder — BlinkLab runs its own (blinklabFrameRecorder.ts). Add
    // .ph-no-capture to a canvas to opt it out if replay size or editor CPU climbs.
    session_recording: { ...REPLAY_MASKING },
  });

  // Super properties: stamped on every captured event so deploys can be
  // compared and prod/preview separated without a person-property join.
  posthog.register({
    app_version:
      process.env.NEXT_PUBLIC_APP_VERSION ??
      process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ??
      "unknown",
    environment: process.env.NEXT_PUBLIC_ENV ?? "production",
  });
}
